import React from 'react';
import PropTypes from 'prop-types';

import FileUploadSection from './FileUploadSection';
import QuickSettings from './QuickSettings';
import GeneratedQuestionsPreview from './GeneratedQuestionsPreview';

/**
 * SimpleWorkflow - Clean & simple workflow for quick question generation
 * Streamlined: Upload → Configure → Generate
 */
function SimpleWorkflow({
  // File Upload
  llmProvider,
  useNativeFileUpload,
  setUseNativeFileUpload,
  uploadedFiles,
  fileContents,
  onFileUpload,
  onRemoveFile,
  formatFileSize,
  
  // Generation Settings
  numberOfQuestions,
  setNumberOfQuestions,
  difficulty,
  setDifficulty,
  includeCodeBlocks,
  setIncludeCodeBlocks,
  userGuidance,
  setUserGuidance,
  topicTags,
  currentTag,
  setCurrentTag,
  onAddTopicTag,
  onRemoveTopicTag,
  
  // Generation
  isGenerating,
  generationLog,
  apiKey,
  onGenerate,
  
  // Results
  generatedQuestions,
  setGeneratedQuestions,
  validationErrors,
  editingIndex,
  editedQuestion,
  setEditedQuestion,
  selectedSession,
  
  // Actions
  getQualityScore,
  findDuplicates,
  validateAllQuestions,
  onImportQuestions,
  onRerollQuestion,
  onGenerateVariation,
  onToggleMultipleChoice,
  onStartEditing,
  onSaveEdit,
  onCancelEdit,
  onSaveToSession,
  onDownloadQuestions,
  onRefineQuestion
}) {
  const hasContent = (fileContents?.length || 0) > 0 || userGuidance?.trim();
  const canGenerate = hasContent && (apiKey || llmProvider === 'custom');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column - Configuration */}
      <div className="space-y-4">
        {/* File Upload - Compact */}
        <FileUploadSection
          llmProvider={llmProvider}
          useNativeFileUpload={useNativeFileUpload}
          setUseNativeFileUpload={setUseNativeFileUpload}
          uploadedFiles={uploadedFiles}
          fileContents={fileContents}
          onFileUpload={onFileUpload}
          onRemoveFile={onRemoveFile}
          formatFileSize={formatFileSize}
        />

        {/* Quick Settings */}
        <QuickSettings
          numberOfQuestions={numberOfQuestions}
          setNumberOfQuestions={setNumberOfQuestions}
          difficulty={difficulty}
          setDifficulty={setDifficulty}
          includeCodeBlocks={includeCodeBlocks}
          setIncludeCodeBlocks={setIncludeCodeBlocks}
          userGuidance={userGuidance}
          setUserGuidance={setUserGuidance}
          topicTags={topicTags}
          currentTag={currentTag}
          setCurrentTag={setCurrentTag}
          onAddTopicTag={onAddTopicTag}
          onRemoveTopicTag={onRemoveTopicTag}
        />

        {/* Generate Button */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <button
            onClick={onGenerate}
            disabled={isGenerating || !canGenerate}
            className={`
              w-full py-3 px-6 rounded-lg font-semibold transition-all
              ${isGenerating || !canGenerate
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            {isGenerating ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Generiere...
              </span>
            ) : (
              <span>✨ {numberOfQuestions} Fragen generieren</span>
            )}
          </button>
          
          {!canGenerate && (
            <p className="text-xs text-center mt-2 text-gray-500 dark:text-gray-400">
              {!hasContent 
                ? '📁 Dateien hochladen oder Anweisungen eingeben'
                : '🔑 API Key fehlt → Einstellungen'
              }
            </p>
          )}
        </div>

        {/* Generation Log */}
        {generationLog && (
          <details className="bg-gray-900 rounded-lg overflow-hidden">
            <summary className="px-4 py-2 text-xs text-gray-400 cursor-pointer hover:bg-gray-800">
              📋 Log anzeigen
            </summary>
            <pre className="px-4 pb-4 text-success-400 text-xs overflow-auto max-h-32 font-mono whitespace-pre-wrap">
              {generationLog}
            </pre>
          </details>
        )}
      </div>

      {/* Right Column - Results */}
      <div>
        {(generatedQuestions?.length || 0) > 0 ? (
          <GeneratedQuestionsPreview
            questions={generatedQuestions}
            setQuestions={setGeneratedQuestions}
            validationErrors={validationErrors}
            editingIndex={editingIndex}
            editedQuestion={editedQuestion}
            setEditedQuestion={setEditedQuestion}
            isGenerating={isGenerating}
            selectedSession={selectedSession}
            getQualityScore={getQualityScore}
            findDuplicates={findDuplicates}
            validateAllQuestions={validateAllQuestions}
            onImportQuestions={onImportQuestions}
            onRerollQuestion={onRerollQuestion}
            onGenerateVariation={onGenerateVariation}
            onToggleMultipleChoice={onToggleMultipleChoice}
            onStartEditing={onStartEditing}
            onSaveEdit={onSaveEdit}
            onCancelEdit={onCancelEdit}
            onSaveToSession={onSaveToSession}
            onDownloadQuestions={onDownloadQuestions}
            onRefineQuestion={onRefineQuestion}
          />
        ) : (
          /* Empty State - Compact */
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6 text-center h-full flex flex-col justify-center min-h-[300px]">
            <div className="text-4xl mb-3">✨</div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-1">
              Bereit
            </h3>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Generierte Fragen erscheinen hier
            </p>
            
            <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-gray-400">
              <span>PDF</span>•<span>DOCX</span>•<span>TXT</span>•<span>MD</span>•<span>Code</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

SimpleWorkflow.propTypes = {
  // File Upload
  llmProvider: PropTypes.string.isRequired,
  useNativeFileUpload: PropTypes.bool.isRequired,
  setUseNativeFileUpload: PropTypes.func.isRequired,
  uploadedFiles: PropTypes.array.isRequired,
  fileContents: PropTypes.array.isRequired,
  onFileUpload: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  formatFileSize: PropTypes.func.isRequired,
  
  // Generation Settings
  numberOfQuestions: PropTypes.number.isRequired,
  setNumberOfQuestions: PropTypes.func.isRequired,
  difficulty: PropTypes.string.isRequired,
  setDifficulty: PropTypes.func.isRequired,
  includeCodeBlocks: PropTypes.bool.isRequired,
  setIncludeCodeBlocks: PropTypes.func.isRequired,
  userGuidance: PropTypes.string,
  setUserGuidance: PropTypes.func,
  topicTags: PropTypes.array.isRequired,
  currentTag: PropTypes.string.isRequired,
  setCurrentTag: PropTypes.func.isRequired,
  onAddTopicTag: PropTypes.func.isRequired,
  onRemoveTopicTag: PropTypes.func.isRequired,
  
  // Generation
  isGenerating: PropTypes.bool.isRequired,
  generationLog: PropTypes.string,
  apiKey: PropTypes.string,
  onGenerate: PropTypes.func.isRequired,
  
  // Results
  generatedQuestions: PropTypes.array.isRequired,
  setGeneratedQuestions: PropTypes.func.isRequired,
  validationErrors: PropTypes.object,
  editingIndex: PropTypes.number,
  editedQuestion: PropTypes.object,
  setEditedQuestion: PropTypes.func,
  selectedSession: PropTypes.string,
  
  // Actions
  getQualityScore: PropTypes.func.isRequired,
  findDuplicates: PropTypes.func.isRequired,
  validateAllQuestions: PropTypes.func.isRequired,
  onImportQuestions: PropTypes.func,
  onRerollQuestion: PropTypes.func.isRequired,
  onGenerateVariation: PropTypes.func.isRequired,
  onToggleMultipleChoice: PropTypes.func.isRequired,
  onStartEditing: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onSaveToSession: PropTypes.func,
  onDownloadQuestions: PropTypes.func.isRequired,
  onRefineQuestion: PropTypes.func // Optional - opens refinement chat
};

export default SimpleWorkflow;
